import { Card } from '@/components/ui/card';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { uploadLogo } from '@/features/workspace/workspaceThunks';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export default function VisualLogo({
  visualIdentity,
}: {
  visualIdentity?: WorkspaceVisualIdentity | null;
}) {
  const { t } = useTranslation('webinars');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((s) => s.workspace.currentWorkspace?.id);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCardClick = () => {
    if (fileInputRef.current && !isUploading) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE_BYTES) {
      setError(t('visualIdentity.logo.fileTooLarge'));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (!workspaceId) return;
    try {
      setIsUploading(true);
      setError(null);
      await dispatch(uploadLogo({ workspaceId, file }));
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  const isDarkTheme = visualIdentity?.theme === 'DARK';
  const backgroundColor = isDarkTheme ? visualIdentity?.backgroundColor || '#FFFFFF' : undefined;

  return (
    <Card
      onClick={handleCardClick}
      className={cn(
        "max-w-[357px] p-0 overflow-hidden cursor-pointer relative transition-colors duration-200 hover:cursor-[url('/assets/edit.svg'),pointer] group",
        !isDarkTheme && 'bg-white hover:bg-[#00000033]',
      )}
      style={isDarkTheme && backgroundColor ? { backgroundColor } : undefined}
    >
      {isDarkTheme && (
        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-200 pointer-events-none" />
      )}
      <div className="w-full h-full flex items-center justify-center">
        <img
          src={visualIdentity?.logoUrl || '/assets/empty-logo.png'}
          alt={t('visualIdentity.logo.alt')}
          className={`max-w-full  max-h-full object-center  ${
            visualIdentity?.logoUrl ? '' : 'object-cover w-full h-full'
          }`}
        />
      </div>
      {isUploading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
          <div className="text-white text-sm">{t('visualIdentity.logo.uploading')}</div>
        </div>
      )}
      {error && (
        <div className="absolute left-0 right-0 bottom-0 text-error text-xs px-2 py-1 text-center">
          {error}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        disabled={isUploading}
      />
    </Card>
  );
}
